import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import * as React from 'react';
import { Badge, Button, EmptyState, LoadingBlock } from '@/components/ui';
import { apiGet, apiPost } from '@/lib/api';
import { cn } from '@/lib/utils';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  linkUrl: string | null;
  readAt: string | null;
  createdAt: string;
}

interface NotificationPayload {
  items: NotificationItem[];
  unreadCount: number;
}

/**
 * Notification bell.
 *
 * Polls on an interval as well as being invalidated by SSE. The poll is the
 * safety net: if the event stream drops on a flaky connection the badge still
 * catches up within a minute, rather than silently showing nothing until the
 * page is reloaded.
 */
export function NotificationBell() {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiGet<NotificationPayload>('/notifications'),
    refetchInterval: 60_000,
  });

  const markAllRead = useMutation({
    mutationFn: () => apiPost('/notifications/read'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const unread = notifications.data?.unreadCount ?? 0;
  const items = notifications.data?.items ?? [];

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        onClick={() => setOpen((value) => !value)}
        className="relative"
      >
        <Bell />
        {unread > 0 && (
          <span className="absolute right-1 top-1 grid min-w-[16px] place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-4 text-destructive-foreground">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-50 mt-1 w-80 animate-fade-in rounded-md border border-border bg-popover shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <p className="text-sm font-semibold">Notifications</p>
              {unread > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => markAllRead.mutate()}
                  loading={markAllRead.isPending}
                >
                  <CheckCheck /> Mark all read
                </Button>
              )}
            </div>

            <div className="scrollbar-thin max-h-80 overflow-y-auto">
              {notifications.isLoading ? (
                <LoadingBlock />
              ) : items.length === 0 ? (
                <EmptyState
                  icon={<Bell className="size-7" />}
                  title="Nothing yet"
                  description="Order updates and alerts will appear here."
                />
              ) : (
                <ul className="divide-y divide-border">
                  {items.map((item) => (
                    <li
                      key={item.id}
                      className={cn('px-3 py-2.5', !item.readAt && 'bg-accent/40')}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium leading-snug">{item.title}</p>
                        {!item.readAt && <Badge tone="info">New</Badge>}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{item.message}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground/70">
                        {new Date(item.createdAt).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                        })}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
